import { useRef } from 'react';
import useSWR, { SWRConfiguration } from 'swr';

export type UseQueryHandler<T> = () => Promise<T>;

export type UseQueryProps<T> = Pick<
  SWRConfiguration<T>,
  'onSuccess' | 'onError' | 'revalidateOnMount' | 'keepPreviousData'
> & {
  namespace?: string;
  tags?: string[];
};

export function useQuery<TReturn>(
  key: string | null,
  handler: UseQueryHandler<TReturn>,
  props: UseQueryProps<TReturn> = {}
) {
  const { namespace = 'domain', tags, ...others } = props;

  const { data, isLoading, mutate, isValidating } = useSWR(
    key && { namespace, key, tags },
    handler,
    {
      ...others,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      errorRetryCount: 0,
    }
  );

  const loadedRef = useRef(isLoading);

  if (!isLoading) {
    loadedRef.current = true;
  }

  return {
    data: data || null,
    loading: isLoading || isValidating,
    loaded: loadedRef.current,
    reload: mutate,
  };
}
